import type { AudioRecording } from "./types";

/**
 * Format bytes into human-readable string
 */
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Format duration in seconds to mm:ss format
 */
export const formatDuration = (seconds: number): string => {
  if (isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/**
 * Extract timestamp from recording key or turn_id
 */
export const extractTimestampFromRecording = (
  recording: AudioRecording,
): Date | null => {
  // Try to extract timestamp from key/filename (format: something_20260116T110208Z.wav)
  const filename = recording.key.split("/").pop() || recording.key;
  const timestampMatch = filename.match(/(\d{8}T\d{6}Z)/);

  if (timestampMatch && timestampMatch[1]) {
    const timestampStr = timestampMatch[1];
    // Parse format: YYYYMMDDTHHmmSSZ
    const year = timestampStr.substring(0, 4);
    const month = timestampStr.substring(4, 6);
    const day = timestampStr.substring(6, 8);
    const hour = timestampStr.substring(9, 11);
    const minute = timestampStr.substring(11, 13);
    const second = timestampStr.substring(13, 15);

    // Create ISO 8601 string: YYYY-MM-DDTHH:mm:ssZ
    const isoString = `${year}-${month}-${day}T${hour}:${minute}:${second}Z`;
    const date = new Date(isoString);

    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  // Fallback: Try to extract Unix timestamp from turn_id
  const turnIdParts = recording.turn_id.split("_");
  const lastPart = turnIdParts[turnIdParts.length - 1];
  if (turnIdParts.length > 1 && lastPart) {
    const timestamp = parseInt(lastPart);
    if (!isNaN(timestamp)) {
      const date =
        timestamp > 10000000000
          ? new Date(timestamp)
          : new Date(timestamp * 1000);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  }

  // Fallback to recording.timestamp if available
  if (recording.timestamp) {
    const date = new Date(recording.timestamp);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  return null;
};

/**
 * Format time from recording timestamp
 */
export const formatTimeFromRecording = (recording: AudioRecording): string => {
  const date = extractTimestampFromRecording(recording);
  if (!date) return "N/A";
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

/**
 * Format a YYYY-MM-DD calendar date string for display without UTC drift.
 * `new Date("2026-06-11")` parses as UTC midnight, which renders as the
 * previous day in any negative-offset locale; this builds a local Date
 * from the parts so the date label matches the calendar date in the key.
 */
export const formatCalendarDate = (dateStr: string): string => {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return dateStr;
  const [, y, m, d] = match;
  const local = new Date(Number(y), Number(m) - 1, Number(d));
  return local.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Rolling window of the last `days` days, ending today, as YYYY-MM-DD.
 *
 * Expressed in UTC because audio keys are pathed by the UTC date the tutor
 * uploaded under; reading the bounds off local time hides today's recordings
 * from any viewer whose local date is behind UTC.
 */
export const getRecentUtcRange = (
  days: number,
  now: Date = new Date(),
): { start_date: string; end_date: string } => {
  const start = new Date(now);
  start.setUTCDate(start.getUTCDate() - (days - 1));
  return {
    start_date: start.toISOString().slice(0, 10),
    end_date: now.toISOString().slice(0, 10),
  };
};
